import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { signIn, signInWithGoogle, resetPassword } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, profile, org } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState('')
  const [resetSent, setResetSent] = useState(false)
  const [showReset, setShowReset] = useState(false)

  // Already signed in — redirect
  if (user && profile) {
    const dest = profile.role === 'superadmin' ? '/superadmin'
      : profile.role === 'lecturer' ? '/lecturer'
      : org ? '/admin' : '/signup'
    navigate(dest, { replace: true })
    return null
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true); setError('')
    const { error: err } = await signIn(email, password)
    if (err) {
      setError(
        err.message?.toLowerCase().includes('invalid') ? 'Incorrect email or password.' :
        err.message?.toLowerCase().includes('confirmed') ? 'Please verify your email first, then try again.' :
        err.message || 'Sign in failed. Try again.'
      )
    }
    setLoading(false)
  }

  async function handleGoogle() {
    setGoogleLoading(true); setError('')
    const { error: err } = await signInWithGoogle()
    if (err) { setError('Google sign-in failed. Try again.'); setGoogleLoading(false) }
    // Success: redirects to /auth/callback automatically
  }

  async function handleReset(e) {
    e.preventDefault()
    if (!email) { setError('Enter your email above first.'); return }
    setLoading(true); setError('')
    await resetPassword(email)
    setResetSent(true); setLoading(false)
  }

  return (
    <div className="page-center">
      <div className="container-sm" style={{ width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div className="logo" style={{ fontSize: 22, marginBottom: 4 }}>Campus<span>Flow</span></div>
          <p className="text-sm text-muted">Sign in to your account</p>
        </div>

        <div className="card fade-up">
          {error && <div className="alert alert-error mb-3">{error}</div>}
          {resetSent && <div className="alert alert-success mb-3">Password reset email sent — check your inbox.</div>}

          {/* Google */}
          <button className="btn btn-google mb-3" onClick={handleGoogle} disabled={googleLoading}>
            {googleLoading ? <span className="spinner" /> : (
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
            )}
            Continue with Google
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span className="text-xs text-muted">or email</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>

          <form onSubmit={showReset ? handleReset : handleSubmit}>
            <div className="form-group">
              <label className="field-label">Email address</label>
              <input type="email" autoFocus required placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            {!showReset && (
              <div className="form-group">
                <label className="field-label">Password</label>
                <input type="password" required placeholder="Your password" value={password} onChange={e => setPassword(e.target.value)} />
              </div>
            )}
            <button type="submit" className="btn btn-primary btn-full mt-2" disabled={loading}>
              {loading ? <span className="spinner" /> : showReset ? 'Send reset link' : 'Sign in'}
            </button>
          </form>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem', fontSize: 12 }}>
            <span style={{ color: 'var(--blue)', cursor: 'pointer' }} onClick={() => { setShowReset(!showReset); setError(''); setResetSent(false) }}>
              {showReset ? '← Back to sign in' : 'Forgot password?'}
            </span>
            <span style={{ color: 'var(--text-muted)' }}>
              No account? <span style={{ color: 'var(--blue)', cursor: 'pointer' }} onClick={() => navigate('/signup')}>Register free</span>
            </span>
          </div>
        </div>

        <p className="text-xs text-muted text-center mt-3">
          Mark attendance? <span style={{ color: 'var(--blue)', cursor: 'pointer' }} onClick={() => navigate('/attend')}>Go to attend portal →</span>
        </p>
      </div>
    </div>
  )
}
